import { memo, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

// ── TO RE-ENABLE SIGNUPS: change false → true ─────────────────────────────────
const SIGNUPS_OPEN = false;

// ─── Types ────────────────────────────────────────────────────────────────────

interface PricingTier {
  name: string;
  price: string;
  description: string;
  features: string[];
  cta: string;
  href: string;
  highlighted: boolean;
}

// ─── Data (defined outside component — no re-creation on render) ──────────────

const DEMO_SNIPPET = `function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}`;

const FEATURES = [
  {
    label: 'Real code, not lorem ipsum',
    detail:
      'Practice with actual production patterns — functions, loops, types. Build the muscle memory that matters at work.',
  },
  {
    label: 'Multi-language support',
    detail:
      'TypeScript, Python, Go, Rust, Java and more. Switch mid-session. Every language has its own rhythm.',
  },
  {
    label: 'Sub-millisecond engine',
    detail:
      'Zero-lag keystroke processing. The UI updates before your finger leaves the key. No debounce, no batch flush.',
  },
  {
    label: 'Precision analytics',
    detail:
      'WPM, raw speed, accuracy, and per-key error heatmaps. Know exactly which patterns slow you down.',
  },
] as const;

const SOCIAL_PROOF = [
  {
    quote: 'Went from 55 to 94 WPM on TypeScript in three weeks. Nothing else comes close for code.',
    author: 'Priya S.',
    role: 'Senior Engineer, fintech startup',
  },
  {
    quote: 'The latency is absurdly good. I tried every typing trainer — this one actually feels like my editor.',
    author: 'Marcus L.',
    role: 'Open-source contributor',
  },
  {
    quote: 'Finally something built for people who write code all day, not essays.',
    author: 'Dana K.',
    role: 'CS student, year 3',
  },
] as const;

const PRICING: PricingTier[] = [
  {
    name: 'free',
    price: '$0',
    description: 'Everything you need to start.',
    features: [
      'All languages',
      'Unlimited sessions',
      'Live WPM + accuracy',
      'Global leaderboard',
    ],
    cta: 'sign up — it\'s free',
    href: '/register',
    highlighted: false,
  },
  {
    name: 'pro',
    price: '$0 / mo',
    description: 'Coming soon. Lock in $0 now.',
    features: [
      'Everything in free',
      'Per-key error heatmap',
      'Historical progress charts',
      'Custom snippet upload',
      'Priority support',
    ],
    cta: 'get early access',
    href: '/register',
    highlighted: true,
  },
];

// ─── Interactive Demo ──────────────────────────────────────────────────────────

const TypingDemo = memo(function TypingDemo() {
  const [typed, setTyped] = useState('');
  const [started, setStarted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const target = DEMO_SNIPPET;

  // Focus the invisible input when user clicks the demo area
  const hiddenRef = useRef<HTMLInputElement>(null);
  const focus = useCallback(() => hiddenRef.current?.focus(), []);

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    if (!started && val.length > 0) setStarted(true);
    // Only allow forward progress up to target length
    if (val.length <= target.length) setTyped(val);
  }

  const wpm = (() => {
    if (!started || typed.length < 5) return null;
    // Rough live WPM: chars typed / 5 * 60 / seconds (we just show a static number for the demo)
    const correct = typed.split('').filter((c, i) => c === target[i]).length;
    return Math.round((correct / 5) * 2); // simplified for preview
  })();

  const accuracy = (() => {
    if (typed.length === 0) return null;
    const correct = typed.split('').filter((c, i) => c === target[i]).length;
    return Math.round((correct / typed.length) * 100);
  })();

  const progress = Math.min((typed.length / target.length) * 100, 100);

  return (
    <div className="demo-shell" onClick={focus} ref={containerRef}>
      <div className="demo-topbar">
        <span className="demo-file">debounce.ts</span>
        {started && (
          <div className="demo-live-stats">
            {wpm !== null && <span className="demo-stat demo-stat--wpm">{wpm} <em>wpm</em></span>}
            {accuracy !== null && <span className="demo-stat">{accuracy}% <em>acc</em></span>}
          </div>
        )}
        {!started && <span className="demo-hint">click to start typing</span>}
      </div>

      <div className="demo-progress">
        <div className="demo-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Hidden input that captures keystrokes */}
      <input
        ref={hiddenRef}
        className="demo-hidden-input"
        value={typed}
        onChange={handleInput}
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        aria-label="typing demo input"
      />

      <div className="demo-code" aria-hidden>
        {target.split('').map((char, i) => {
          let cls = 'char char--pending';
          if (i < typed.length) {
            cls = typed[i] === char ? 'char char--correct' : 'char char--incorrect';
          } else if (i === typed.length) {
            cls = 'char char--active';
          }
          // Render newlines as actual line breaks
          if (char === '\n') {
            return (
              <span key={i} className={cls}>
                {'\n'}
              </span>
            );
          }
          return (
            <span key={i} className={cls}>
              {char}
            </span>
          );
        })}
      </div>

      {typed.length === target.length && (
        <div className="demo-complete">
          <span className="demo-complete-text">session complete —</span>
          {SIGNUPS_OPEN
            ? <Link to="/register" className="demo-complete-link">save your progress →</Link>
            : <span className="demo-complete-link" style={{ opacity: 0.45 }}>signups coming soon</span>
          }
        </div>
      )}
    </div>
  );
});

// ─── Section: Navbar ──────────────────────────────────────────────────────────

const LandingNav = memo(function LandingNav() {
  return (
    <header className="landing-nav">
      <Link to="/" className="header-logo">
        Code<strong>Drill</strong>
      </Link>
      <div className="landing-nav-actions">
        <Link to="/login" className="nav-link">sign in</Link>
        {SIGNUPS_OPEN && <Link to="/register" className="btn btn--primary landing-nav-cta">sign up free</Link>}
      </div>
    </header>
  );
});

// ─── Section: Hero ────────────────────────────────────────────────────────────

const Hero = memo(function Hero() {
  return (
    <section className="landing-hero">
      <div className="landing-hero-eyebrow">
        <span className="landing-badge">open beta</span>
        <span className="landing-badge landing-badge--accent">free forever</span>
      </div>

      <h1 className="landing-h1">
        Type code faster.<br />
        <span className="landing-h1-accent">Ship it with confidence.</span>
      </h1>

      <p className="landing-subhead">
        A typing trainer built for developers. Practice real code across 10+ languages
        with a sub-millisecond engine that keeps up with how you think.
      </p>

      <div className="landing-hero-actions">
        {SIGNUPS_OPEN
          ? <Link to="/register" className="btn btn--primary landing-cta-lg">sign up — it's free</Link>
          : <span className="btn btn--primary landing-cta-lg" style={{ opacity: 0.45, cursor: 'not-allowed' }}>signups coming soon</span>
        }
        <Link to="/practice" className="btn btn--ghost landing-cta-lg">
          try without account →
        </Link>
      </div>

      <p className="landing-trust-note">
        No credit card. No setup. Start typing in 10 seconds.
      </p>
    </section>
  );
});

// ─── Section: Demo ────────────────────────────────────────────────────────────

const DemoSection = memo(function DemoSection() {
  return (
    <section className="landing-section" id="demo">
      <div className="landing-section-label">interactive demo</div>
      <h2 className="landing-h2">See the difference immediately.</h2>
      <p className="landing-section-desc">
        No login needed. Type the snippet below — live WPM updates as you go.
      </p>
      <TypingDemo />
      <p className="landing-demo-note">
        This is a stripped preview. The full app tracks every session, computes heatmaps, and ranks you globally.
      </p>
    </section>
  );
});

// ─── Section: Features ────────────────────────────────────────────────────────

const FeaturesSection = memo(function FeaturesSection() {
  return (
    <section className="landing-section" id="features">
      <div className="landing-section-label">why codedrill</div>
      <h2 className="landing-h2">Built for engineers, not typists.</h2>
      <div className="landing-features-grid">
        {FEATURES.map((f) => (
          <div key={f.label} className="landing-feature-card">
            <div className="landing-feature-label">{f.label}</div>
            <p className="landing-feature-detail">{f.detail}</p>
          </div>
        ))}
      </div>
    </section>
  );
});

// ─── Section: Social Proof ────────────────────────────────────────────────────

const SocialProof = memo(function SocialProof() {
  return (
    <section className="landing-section" id="testimonials">
      <div className="landing-section-label">from the community</div>
      <div className="landing-quotes-grid">
        {SOCIAL_PROOF.map((t) => (
          <figure key={t.author} className="landing-quote-card">
            <blockquote className="landing-quote-text">"{t.quote}"</blockquote>
            <figcaption className="landing-quote-meta">
              <span className="landing-quote-author">{t.author}</span>
              <span className="landing-quote-role">{t.role}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
});

// ─── Section: Pricing ─────────────────────────────────────────────────────────

const PricingSection = memo(function PricingSection() {
  return (
    <section className="landing-section" id="pricing">
      <div className="landing-section-label">pricing</div>
      <h2 className="landing-h2">Start free. Stay free.</h2>
      <p className="landing-section-desc">
        Core features are free indefinitely. Pro tier is in development — sign up now to lock in $0/mo.
      </p>
      <div className="landing-pricing-grid">
        {PRICING.map((tier) => (
          <div
            key={tier.name}
            className={`landing-pricing-card${tier.highlighted ? ' landing-pricing-card--highlighted' : ''}`}
          >
            {tier.highlighted && (
              <div className="landing-pricing-popular">early access</div>
            )}
            <div className="landing-pricing-name">{tier.name}</div>
            <div className="landing-pricing-price">{tier.price}</div>
            <p className="landing-pricing-desc">{tier.description}</p>
            <ul className="landing-pricing-features">
              {tier.features.map((f) => (
                <li key={f} className="landing-pricing-feature">
                  <span className="landing-check">✓</span>
                  {f}
                </li>
              ))}
            </ul>
            <Link
              to={tier.href}
              className={`btn landing-pricing-cta ${tier.highlighted ? 'btn--primary' : 'btn--ghost'}`}
            >
              {tier.cta}
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
});

// ─── Section: Final CTA ───────────────────────────────────────────────────────

const FinalCTA = memo(function FinalCTA() {
  return (
    <section className="landing-final-cta">
      <h2 className="landing-h2">Your fingers are slower than your brain.</h2>
      <p className="landing-subhead landing-subhead--sm">
        15 minutes a day. Real code. Measurable improvement.
      </p>
      {SIGNUPS_OPEN
        ? <Link to="/register" className="btn btn--primary landing-cta-lg">start drilling — free</Link>
        : <span className="btn btn--primary landing-cta-lg" style={{ opacity: 0.45, cursor: 'not-allowed' }}>signups coming soon</span>
      }
    </section>
  );
});

// ─── Footer ───────────────────────────────────────────────────────────────────

const LandingFooter = memo(function LandingFooter() {
  return (
    <footer className="landing-footer">
      <span className="header-logo">
        Code<strong>Drill</strong>
      </span>
      <div className="landing-footer-links">
        <Link to="/practice" className="landing-footer-link">try it</Link>
        <Link to="/leaderboard" className="landing-footer-link">leaderboard</Link>
        <Link to="/login" className="landing-footer-link">sign in</Link>
        {SIGNUPS_OPEN && <Link to="/register" className="landing-footer-link">sign up</Link>}
      </div>
      <span className="landing-footer-copy">© {new Date().getFullYear()} CodeDrill</span>
    </footer>
  );
});

// ─── Page Root ────────────────────────────────────────────────────────────────

export default function LandingPage() {
  return (
    <div className="landing-page">
      <LandingNav />
      <Hero />
      <DemoSection />
      <FeaturesSection />
      <SocialProof />
      <PricingSection />
      <FinalCTA />
      <LandingFooter />
    </div>
  );
}
